import confetti from "canvas-confetti";

import { useHaptics } from "@/modules/haptics";

/**
 * Custom hook for triggering confetti animations with haptic feedback
 * Works on both web and mobile platforms:
 * - Confetti animations work universally via canvas-confetti
 * - Haptics only trigger on native mobile platforms (safely no-op on web)
 */
export const useConfetti = () => {
  const { triggerHaptic } = useHaptics();

  const animateConfetti = () => {
    const count = 200;

    // Luxury gold color palette - various shades of gold, champagne, and tan
    const colors = [
      "#D4AF37", // Gold
      "#CFB53B", // Dark gold
      "#F0E68C", // Light gold/khaki
      "#DAA520", // Goldenrod
      "#C9B037", // Metallic gold
      "#B8860B", // Dark goldenrod
      "#F4E4C1", // Champagne
      "#E6C794", // Tan gold
    ];

    const defaults = {
      origin: { y: 0.95 }, // Start from bottom of viewport
      gravity: 0.8, // Moderate gravity for elegant fall
      ticks: 500, // Longer duration to see full animation (up and down)
      colors,
      drift: 0.3, // Gentle horizontal drift
    };

    const fire = (particleRatio: number, opts: confetti.Options) => {
      confetti({
        ...defaults,
        ...opts,
        particleCount: Math.floor(count * particleRatio),
      });
    };

    // Create custom SVG shapes matching the design
    // Circle shape
    const circleShape = confetti.shapeFromPath({
      path: "M10,5 A5,5 0 1,1 10,5 Z",
    });

    // 5-pointed star
    const fivePointStar = confetti.shapeFromPath({
      path: "M10,0 L12.4,7.6 L20,9.3 L14.5,14.5 L16,22 L10,18 L4,22 L5.5,14.5 L0,9.3 L7.6,7.6 Z",
    });

    // Diamond (rotated square)
    const diamondShape = confetti.shapeFromPath({
      path: "M10,0 L20,10 L10,20 L0,10 Z",
    });

    // Small square/rectangle
    const squareShape = confetti.shapeFromPath({
      path: "M0,0 L10,0 L10,10 L0,10 Z",
    });

    // Curved ribbon/streamer
    const ribbonShape = confetti.shapeFromPath({
      path: "M0,0 Q5,5 0,10 Q5,15 0,20 Q5,25 0,30",
    });

    // Mix of all custom shapes
    const allShapes = [circleShape, fivePointStar, diamondShape, squareShape, ribbonShape];

    // First burst - shoots high to reach the top
    fire(0.3, {
      spread: 55,
      startVelocity: 90, // High velocity to reach top
      shapes: allShapes,
      scalar: 0.8,
    });

    // Second burst - stars and circles shooting high
    fire(0.25, {
      spread: 70,
      startVelocity: 85,
      shapes: [fivePointStar, circleShape],
      scalar: 1.0,
    });

    // Third burst - wide spread reaching top
    fire(0.25, {
      spread: 90,
      startVelocity: 95, // Highest velocity
      decay: 0.88,
      scalar: 0.9,
      shapes: allShapes,
    });

    // Fourth burst - diamonds and ribbons for depth
    fire(0.15, {
      spread: 80,
      startVelocity: 75,
      decay: 0.9,
      scalar: 1.5,
      shapes: [diamondShape, squareShape, ribbonShape],
    });

    // Final burst - fast shooting particles
    fire(0.05, {
      spread: 100,
      startVelocity: 100, // Very fast to reach top
      scalar: 0.7,
      shapes: allShapes,
    });
  };

  const triggerConfetti = () => {
    animateConfetti();
    // Haptics will safely no-op on web platforms
    void triggerHaptic("reward").catch(() => {
      // Silently handle any haptics errors (e.g., on web where haptics aren't available)
    });
  };

  return {
    triggerConfetti,
  };
};
